/**
 * Laravel / axios error helpers for financial reports (balance sheet, income statement).
 * 422: use response.data.errors (array per field) and response.data.message.
 * 5xx: generic guidance + retry hint.
 */

/**
 * @param {object} data — parsed JSON body from error.response.data
 * @returns {string[]}
 */
export function flattenLaravelErrors(data) {
	if (!data || typeof data !== "object") return [];
	const errs = data.errors;
	if (!errs || typeof errs !== "object") return [];
	const lines = [];
	for (const val of Object.values(errs)) {
		if (Array.isArray(val)) {
			for (const m of val) {
				if (m != null && String(m).trim() !== "") lines.push(String(m).trim());
			}
		} else if (val != null && String(val).trim() !== "") {
			lines.push(String(val).trim());
		}
	}
	return lines;
}

/**
 * Human-readable message for toast / alert (never rely on a bare "Server error").
 * @param {import('axios').AxiosError} error
 * @returns {string}
 */
export function formatFinancialReportErrorMessage(error) {
	const status = error?.response?.status;
	const data = error?.response?.data;

	if (status === 422 && data && typeof data === "object") {
		const fieldLines = flattenLaravelErrors(data);
		const top =
			typeof data.message === "string" && data.message.trim()
				? data.message.trim()
				: "";
		const parts = [];
		if (top && !fieldLines.some((l) => l.includes(top))) parts.push(top);
		parts.push(...fieldLines);
		const seen = new Set();
		const deduped = parts.filter((p) => {
			if (seen.has(p)) return false;
			seen.add(p);
			return true;
		});
		if (deduped.length) return deduped.join(" ");
		return "The request could not be validated. Check your dates and try again.";
	}

	if (status >= 500) {
		return "The server could not complete this report. Please wait a moment, then open the filter and try again.";
	}

	if (status === 404) {
		return typeof data?.message === "string" && data.message.trim()
			? data.message.trim()
			: "Report was not found.";
	}

	if (!error?.response) {
		return error?.message && String(error.message).trim()
			? String(error.message).trim()
			: "Network error. Check your connection and try again.";
	}

	const msg = data?.message || error?.message;
	if (typeof msg === "string" && msg.trim()) return msg.trim();
	return "Unable to load the report. Please try again.";
}

/**
 * @param {import('vue').default} vm — component instance with $bvToast
 * @param {import('axios').AxiosError} error
 */
export function showFinancialReportApiError(vm, error) {
	const body = formatFinancialReportErrorMessage(error);
	const status = error?.response?.status;
	const title = status === 422 ? "Could not run report" : "Report error";
	if (vm?.$bvToast) {
		vm.$bvToast.toast(body, {
			title,
			variant: "danger",
			autoHideDelay: status === 422 ? 12000 : 7000,
			solid: true,
			appendToast: true,
		});
	} else {
		console.error(error);
	}
}
